/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { deviceIntegrationsTestConnection } from "../funcs/deviceIntegrationsTestConnection.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { DeviceIntegrationTestConnectionResult } from "../models/components/deviceintegrationtestconnectionresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  TestDeviceIntegrationConnectionRequest,
  TestDeviceIntegrationConnectionSecurity,
} from "../models/operations/testdeviceintegrationconnection.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type TestDeviceIntegrationConnectionMutationVariables = {
  request: TestDeviceIntegrationConnectionRequest;
  security?: TestDeviceIntegrationConnectionSecurity | undefined;
  options?: RequestOptions;
};

export type TestDeviceIntegrationConnectionMutationData =
  DeviceIntegrationTestConnectionResult;

export type TestDeviceIntegrationConnectionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * testConnection deviceIntegrations
 *
 * @remarks
 * Probe the provider with the stored credentials to verify they work. Save the config first; this tests what is stored, so secrets never round-trip.
 */
export function useTestDeviceIntegrationConnectionMutation(
  options?: MutationHookOptions<
    TestDeviceIntegrationConnectionMutationData,
    TestDeviceIntegrationConnectionMutationError,
    TestDeviceIntegrationConnectionMutationVariables
  >,
): UseMutationResult<
  TestDeviceIntegrationConnectionMutationData,
  TestDeviceIntegrationConnectionMutationError,
  TestDeviceIntegrationConnectionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildTestDeviceIntegrationConnectionMutation(client, options),
    ...options,
  });
}

export function mutationKeyTestDeviceIntegrationConnection(): MutationKey {
  return ["@gram/client", "deviceIntegrations", "testConnection"];
}

export function buildTestDeviceIntegrationConnectionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: TestDeviceIntegrationConnectionMutationVariables,
  ) => Promise<TestDeviceIntegrationConnectionMutationData>;
} {
  return {
    mutationKey: mutationKeyTestDeviceIntegrationConnection(),
    mutationFn: function testDeviceIntegrationConnectionMutationFn({
      request,
      security,
      options,
    }): Promise<TestDeviceIntegrationConnectionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(deviceIntegrationsTestConnection(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
